import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikProps } from 'formik';
import { Button, Col, Row } from 'antd';
import { LoadingOutlined, FileProtectOutlined } from '@ant-design/icons';

import { I18nNameSpaces } from 'common/services/i18n';
import { ClientVerificationFormPhoneValues } from 'common/models/Forms';
import { InputSearchField } from 'common/fields';
import { ResultMessage } from 'common/components';
import { makeCodeSchema } from './schema';

interface Props {
  readOnly?: boolean;
  signature?: string | null;
  success?: string | null;
  error?: ReactNode;
  loadingRequest?: boolean;
  disableRequest?: boolean;
  loadingVerify?: boolean;
  disableVerify?: boolean;

  onRequestCode: () => void;
  onVerifyCode: (code: string) => void;
}

export default function SignatureForm({
  signature,
  readOnly,
  success,
  error,
  loadingRequest,
  disableRequest,
  loadingVerify,
  disableVerify,
  onRequestCode,
  onVerifyCode,
}: Props) {
  const { t } = useTranslation(I18nNameSpaces.Forms);

  const codeSchema = makeCodeSchema(t);

  function renderCodeVerifyForm({ submitForm }: FormikProps<Pick<ClientVerificationFormPhoneValues, 'code'>>) {
    return (
      <Form>
        <InputSearchField
          name="code"
          label={t('signatureForm.code.label')}
          placeholder={t('signatureForm.code.placeholder')}
          autoComplete="off"
          enterButton={loadingVerify ? <LoadingOutlined /> : t('signatureForm.sign.submit')}
          onSearch={submitForm}
          disabled={readOnly || loadingVerify || disableVerify}
        />
      </Form>
    );
  }

  return (
    <div className="signature-form">
      <Button
        className="btn-request-code"
        type="primary"
        block
        disabled={readOnly || disableRequest || loadingRequest}
        onClick={onRequestCode}
      >
        {t('signatureForm.request.submit')}
        {loadingRequest ? <LoadingOutlined /> : <FileProtectOutlined />}
      </Button>
      <Formik
        initialValues={{
          code: '',
        }}
        onSubmit={({ code }) => {
          onVerifyCode(code);
        }}
        validationSchema={codeSchema}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {renderCodeVerifyForm}
      </Formik>

      <Row justify="space-between" gutter={30}>
        <Col span={24} md={16}>
          {Boolean(error) && <ResultMessage type="error">{error}</ResultMessage>}
          {Boolean(success) && !error && <ResultMessage type="success">{success}</ResultMessage>}
        </Col>
        <Col span={24} md={8} className="signature-area">
          {readOnly || !signature ? (
            <span className="signature-unknown">{t('signatureForm.signature.placeholder')}</span>
          ) : (
            <span>
              <span className="signature-prefix">{t('signatureForm.signature.prefix')}</span>
              <span className="signature">{signature}</span>
            </span>
          )}
        </Col>
      </Row>
    </div>
  );
}
